import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send } from 'lucide-react';
import axios from 'axios';
import { SEOHead } from '@/components/SEOHead';
import { AnalyticalField } from '@/components/AnalyticalField';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GridBg = () => (
  <div className="absolute inset-0 grid-texture pointer-events-none" aria-hidden="true" />
);

export function Contact() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', organization: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/contact`, form);
      navigate('/thank-you');
    } catch (err) {
      setError('Submission failed. Please try again or email us directly.');
      setSubmitting(false);
    }
  };

  const canSubmit = form.name && form.organization && form.email && form.message;

  return (
    <div className="bg-zinc-950">
      <SEOHead />
      <section className="relative section-border pt-10 pb-6 md:pt-12 md:pb-8 overflow-hidden">
        <GridBg />
        <AnalyticalField variant="quiet" focalPosition="0.82,0.16" intensity={0.10} />
        <div className="container-site relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <div>
              <span className="text-xs font-mono tracking-widest uppercase text-amber-500 block mb-5">Get in Touch</span>
              <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-zinc-100 leading-tight mb-6">Contact</h1>
              <p className="text-base text-zinc-400 leading-relaxed mb-8">
                For engagement inquiries, procurement questions or general correspondence.
                If you are ready to begin a Pre Diagnostics Session, use the Intake Protocol instead.
              </p>

              <div className="space-y-5">
                <div className="border-l-2 border-zinc-800 pl-5">
                  <div className="text-xs font-mono tracking-widest uppercase text-zinc-500 mb-1">Operated by</div>
                  <div className="text-sm text-zinc-300">Avyria LLC</div>
                </div>
                <div className="border-l-2 border-zinc-800 pl-5">
                  <div className="text-xs font-mono tracking-widest uppercase text-zinc-500 mb-1">For OnPoint Diagnostics inquiries</div>
                  <div className="text-sm text-zinc-400">Complete the intake protocol for fastest response</div>
                </div>
                <div className="border-l-2 border-zinc-800 pl-5">
                  <div className="text-xs font-mono tracking-widest uppercase text-zinc-500 mb-1">Response time</div>
                  <div className="text-sm text-zinc-400">1–2 business days</div>
                </div>
                  <div className="border-l-2 border-zinc-800 pl-5">
                  <div className="text-xs font-mono tracking-widest uppercase text-zinc-500 mb-1">DIRECT</div>
                  <div className="text-sm text-zinc-400">contact@onpoint.works</div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="sr-only">Get in Touch</h2>
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-mono tracking-widest uppercase text-zinc-300 mb-2">
                      Full Name <span className="text-amber-500">*</span>
                    </label>
                    <input
                      value={form.name}
                      onChange={(e) => update('name', e.target.value)}
                      placeholder="Tekkel Schenkel"
                      data-testid="contact-name"
                      required
                      className="w-full bg-zinc-900 border border-zinc-800 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 rounded-md px-4 py-3 text-sm text-zinc-200 placeholder:text-zinc-600 outline-none transition-colors duration-200"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-mono tracking-widest uppercase text-zinc-300 mb-2">
                      Organization <span className="text-amber-500">*</span>
                    </label>
                    <input
                      value={form.organization}
                      onChange={(e) => update('organization', e.target.value)}
                      placeholder="Acme Corp"
                      data-testid="contact-organization"
                      required
                      className="w-full bg-zinc-900 border border-zinc-800 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 rounded-md px-4 py-3 text-sm text-zinc-200 placeholder:text-zinc-600 outline-none transition-colors duration-200"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-zinc-300 mb-2">
                    Business Email <span className="text-amber-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    placeholder="jane@acme.com"
                    data-testid="contact-email"
                    required
                    className="w-full bg-zinc-900 border border-zinc-800 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 rounded-md px-4 py-3 text-sm text-zinc-200 placeholder:text-zinc-600 outline-none transition-colors duration-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-zinc-300 mb-2">
                    Message <span className="text-amber-500">*</span>
                  </label>
                  <textarea
                    value={form.message}
                    onChange={(e) => update('message', e.target.value)}
                    placeholder="Describe your inquiry..."
                    data-testid="contact-message"
                    required
                    rows={5}
                    className="w-full bg-zinc-900 border border-zinc-800 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 rounded-md px-4 py-3 text-sm text-zinc-200 placeholder:text-zinc-600 outline-none transition-colors duration-200 resize-none"
                  />
                </div>

                {error && (
                  <p data-testid="contact-error" className="text-sm text-memo-warn bg-memo-warn-bg border border-memo-warn px-4 py-3 rounded-md">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={!canSubmit || submitting}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-zinc-100 text-zinc-900 rounded-md text-sm font-medium hover:bg-white transition-colors duration-200 disabled:opacity-80 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                  <Send size={14} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
